/**
 * Registers the tray-notification service worker and keeps a ready registration.
 */

let registrationPromise = null;

export function isNotificationServiceWorkerSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof Notification !== "undefined" &&
    window.isSecureContext === true
  );
}

function waitForActive(reg, timeoutMs = 4000) {
  if (reg?.active) return Promise.resolve(reg);
  return new Promise((resolve) => {
    const sw = reg?.installing || reg?.waiting;
    if (!sw) {
      resolve(reg);
      return;
    }
    const done = () => resolve(reg);
    const t = setTimeout(done, timeoutMs);
    sw.addEventListener("statechange", () => {
      if (sw.state === "activated" || sw.state === "redundant") {
        clearTimeout(t);
        done();
      }
    });
    // Nudge waiting workers to activate.
    if (reg.waiting) {
      reg.waiting.postMessage({ type: "SKIP_WAITING" });
    }
  });
}

/**
 * Register (idempotent) and return the ServiceWorkerRegistration, or null.
 */
export function registerNotificationServiceWorker() {
  if (!isNotificationServiceWorkerSupported()) {
    return Promise.resolve(null);
  }
  if (registrationPromise) return registrationPromise;

  registrationPromise = navigator.serviceWorker
    .register("/sw-notifications.js", { scope: "/" })
    .then(async (reg) => {
      await navigator.serviceWorker.ready;
      await waitForActive(reg);
      return reg;
    })
    .catch((err) => {
      console.warn("notification SW register failed", err);
      registrationPromise = null;
      return null;
    });

  return registrationPromise;
}

export async function getNotificationServiceWorkerRegistration() {
  if (!isNotificationServiceWorkerSupported()) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration("/");
    if (existing) {
      await waitForActive(existing);
      return existing;
    }
  } catch (_e) {
    // ignore
  }
  return registerNotificationServiceWorker();
}

/**
 * Navigate the SPA when a tray notification is clicked.
 */
export function bindNotificationClickNavigation() {
  if (!isNotificationServiceWorkerSupported()) return () => {};

  const onMessage = (event) => {
    const data = event.data || {};
    if (data.type !== "NOTIFICATION_CLICK") return;
    const path = data.url || "/kryefaqja";
    try {
      window.focus();
      if (typeof path === "string") {
        const target = path.startsWith("http")
          ? path
          : `${window.location.origin}${path.startsWith("/") ? "" : "/"}${path}`;
        if (target.startsWith(window.location.origin)) {
          const relative = target.slice(window.location.origin.length) || "/";
          if (relative !== window.location.pathname + window.location.search) {
            window.location.assign(relative);
          }
        } else {
          window.location.assign(target);
        }
      }
    } catch (_e) {
      // ignore
    }
  };

  navigator.serviceWorker.addEventListener("message", onMessage);
  return () => {
    navigator.serviceWorker.removeEventListener("message", onMessage);
  };
}
